import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useT } from "@/i18n/LanguageContext";

const EMAIL = "contact@klairys.fr";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useT();
  const tc = t.contact;
  const tf = tc.form;
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    sector: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(tf.errorRequired);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        company: form.company.trim() || null,
        phone: form.phone.trim() || null,
        sector: form.sector || null,
        locale: lang,
      });
      setSubmitted(true);
      toast.success(tf.success);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : tf.errorRequired);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-slate-950 text-white py-28 lg:py-40 overflow-hidden"
    >
      {/* Decorative gradient blobs */}
      <div
        className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#005BEA] opacity-20 blur-[120px] pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#FF6B00] opacity-15 blur-[120px] pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: Heading + contact info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="text-[#FF6B00] text-xs tracking-[0.24em] uppercase font-bold">
                {tc.overline}
              </span>
              <div className="h-px w-16 bg-white/30" />
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.02]">
              {tc.title}
            </h2>
            <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-md">
              {tc.body}
            </p>

            <div className="mt-12 space-y-6">
              <ContactRow
                icon={Mail}
                label={tc.emailLabel}
                value={EMAIL}
                href={`mailto:${EMAIL}`}
              />
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 lg:p-10">
              {submitted ? (
                <div
                  data-testid="contact-success"
                  className="flex flex-col items-center justify-center text-center py-16"
                >
                  <div className="w-16 h-16 rounded-full bg-[#FF6B00]/20 border border-[#FF6B00]/40 flex items-center justify-center mb-6">
                    <CheckCircle2 className="w-8 h-8 text-[#FF6B00]" />
                  </div>
                  <h3 className="font-display text-2xl font-semibold mb-3">
                    {tf.successTitle}
                  </h3>
                  <p className="text-white/65 max-w-md">
                    {tf.successBody}
                  </p>
                  <Button
                    onClick={() => {
                      setSubmitted(false);
                      setForm({
                        name: "",
                        company: "",
                        email: "",
                        phone: "",
                        sector: "",
                        message: "",
                      });
                    }}
                    variant="outline"
                    className="mt-8 bg-white/5 border-white/20 hover:bg-white/10 text-white hover:text-white"
                  >
                    {tf.successAgain}
                  </Button>
                </div>
              ) : (
                <form
                  data-testid="contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label={tf.name} htmlFor="name">
                      <Input
                        id="name"
                        data-testid="contact-input-name"
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        placeholder={tf.namePh}
                        className="bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#005BEA]"
                        required
                      />
                    </Field>
                    <Field label={tf.company} htmlFor="company">
                      <Input
                        id="company"
                        data-testid="contact-input-company"
                        value={form.company}
                        onChange={(e) => update("company", e.target.value)}
                        placeholder={tf.companyPh}
                        className="bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#005BEA]"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label={tf.email} htmlFor="email">
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-input-email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder={tf.emailPh}
                        className="bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#005BEA]"
                        required
                      />
                    </Field>
                    <Field label={tf.phone} htmlFor="phone">
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder={tf.phonePh}
                        className="bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#005BEA]"
                      />
                    </Field>
                  </div>

                  <Field label={tf.sector} htmlFor="sector">
                    <Select
                      value={form.sector}
                      onValueChange={(v) => update("sector", v)}
                    >
                      <SelectTrigger
                        id="sector"
                        data-testid="contact-select-sector"
                        className="bg-white/5 border-white/15 text-white focus:ring-[#005BEA]"
                      >
                        <SelectValue placeholder={tf.sectorPlaceholder} />
                      </SelectTrigger>
                      <SelectContent>
                        {tc.sectors.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label={tf.message} htmlFor="message">
                    <Textarea
                      id="message"
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder={tf.messagePh}
                      rows={5}
                      className="bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#005BEA] resize-none"
                      required
                    />
                  </Field>

                  <Button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={submitting}
                    className="w-full bg-[#FF6B00] hover:bg-[#e55f00] text-white h-13 py-4 rounded-full font-semibold text-[15px] group disabled:opacity-60 shadow-[0_10px_40px_rgba(255,107,0,0.4)]"
                  >
                    {submitting ? (
                      tf.sending
                    ) : (
                      <span className="inline-flex items-center justify-center gap-2">
                        {tf.submit}
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    )}
                  </Button>

                  <p className="text-xs text-white/45 text-center">
                    {tf.legal}
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-2">
      <Label
        htmlFor={htmlFor}
        className="text-[11px] tracking-[0.18em] uppercase font-semibold text-white/65"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href }) {
  const inner = (
    <>
      <div className="w-11 h-11 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-[#005BEA] group-hover:border-[#005BEA] transition-colors">
        <Icon className="w-4 h-4 text-white" strokeWidth={2.2} />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-white/50 mb-1">
          {label}
        </div>
        <div className="text-white font-medium">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} className="flex items-center gap-4 group">
      {inner}
    </a>
  ) : (
    <div className="flex items-center gap-4">{inner}</div>
  );
}
